/**
 * Vite chunk preload recovery.
 *
 * When a new deployment replaces `dist/assets/*`, a browser tab that still holds
 * the previous `index.html` in memory will request a hashed chunk that no longer
 * exists on the origin. Vite dispatches `vite:preloadError` on `window` when a
 * dynamic import fails.
 *
 * Reloading once fetches the fresh `index.html` and resolves the new bundle
 * graph. A session-scoped cooldown prevents an infinite reload loop when the
 * failure is a genuine network outage rather than a deployment rollover.
 */
import type { ChunkRecoveryOptions } from '@/app/version/types';

export const CHUNK_RELOAD_STORAGE_KEY = 'mana-locals:chunk-reload-at';
export const DEFAULT_CHUNK_RELOAD_COOLDOWN_MS = 15_000;

function defaultStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

function defaultReload(): void {
  globalThis.location?.reload();
}

function shouldReload(
  storage: Pick<Storage, 'getItem' | 'setItem'> | null,
  now: number,
  cooldownMs: number,
): boolean {
  if (!storage) return true;

  const raw = storage.getItem(CHUNK_RELOAD_STORAGE_KEY);
  const lastReloadAt = raw ? Number(raw) : 0;

  if (Number.isFinite(lastReloadAt) && lastReloadAt > 0 && now - lastReloadAt < cooldownMs) {
    return false;
  }

  try {
    storage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
  } catch {
    // Quota or private-mode restriction — proceed with reload once.
  }
  return true;
}

export function registerChunkErrorRecovery(options: ChunkRecoveryOptions = {}): () => void {
  const {
    reload = defaultReload,
    storage = defaultStorage(),
    now = Date.now,
    cooldownMs = DEFAULT_CHUNK_RELOAD_COOLDOWN_MS,
  } = options;

  const onPreloadError = (event: Event) => {
    if (!shouldReload(storage, now(), cooldownMs)) return;
    event.preventDefault();
    reload();
  };

  globalThis.addEventListener('vite:preloadError', onPreloadError);

  return () => {
    globalThis.removeEventListener('vite:preloadError', onPreloadError);
  };
}
